import React, { useState, useEffect, useContext } from 'react'

import ASN, { PrimaryRefiners, RefinerVal } from '../services/ASN'
import AsnDialogContext, { MODAL_TYPE } from '../asnDialog/Context'
import GenericModal from '../modal/Modal'
import AsnDialog from '../asnDialog/AsnDialog'
import RoleContext, { CanEdit } from '../signin/RoleContext'
import LPContext, { DEFAULT_LP } from '../lpLoader/LPContext'
import PrimaryASNFilterDropdown, {singleModeOption} from '../asnListDropdowns/primaryASNFilterDropdown'
import SortingDropdown from '../asnListDropdowns/sortingDropdown'
import InfiniteScrollWrapper from './InfiniteScrollWrapper'
import { Link } from 'react-router-dom'
import { FeatureFlags, FeatureFlagsType } from '../stores/FeatureFlag'
import CallToken from '../settings/CallToken'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faSlidersH } from '@fortawesome/free-solid-svg-icons'

const ASNList = () => {
  const [error, setError] = useState(false);
  const [asn_list, setAsn_list] = useState<any[]>([]);
  const [offset, setOffset] = useState(0);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [filterValues, setFilterValues] = useState([] as RefinerVal[]);
  const [showRemoved, setShowRemoved] = useState(false);
  const [sortValue, setSortValue] = useState('default');
  const [isFiltering, setIsFiltering] = useState(false);
  const roleContext = useContext(RoleContext);
  const lpContext = useContext(LPContext);
  const [currentLpId, setCurrentLpId] = useState(lpContext.LP.id);
  const [isReversed, setIsReversed] = useState(false);
  const { useCases } = React.useContext(FeatureFlags) as FeatureFlagsType;

  const getASNs = (currOffset: number = offset) => {
    if (lpContext.LP.id !== DEFAULT_LP.id) {
      //reversed = descending, which is the desired default. Flip here to map cleanly to back end
      return ASN.getList(lpContext.LP.id, currOffset, sortValue, filterValues, !isReversed)
    } else {
      return Promise.resolve({ content: { value: [] } });
    }
  }

  const updateFilters = (refiner: string, newVal: string) => {
    let newVals = [ ...filterValues ];

    if (!newVal || (newVal === 'none')) {
      if (filterValues.some((item) => item.refiner === refiner)) {
          newVals = newVals.filter((item) => item.refiner !== refiner);
          setFilterValues(newVals);
      }
    } else {
      const currItem = filterValues.find((item) => item.refiner === refiner);
      if (currItem) {
        currItem.value = newVal;
      } else {
        newVals.push({ refiner: refiner, value: newVal })
      }

      setFilterValues(newVals);
    }
  }

  const resetList = (lpChanged: boolean = false) => {
    setOffset(0)
    activeFetch(0, 0, lpChanged)
    setHasMore(true)
    //setLoading(false)
  }

  const lpLoaderResetList = (lpChanged: boolean = false) => {
    setOffset(0)
    setHasMore(true)
    activeFetch(0, 0, lpChanged)
    //setLoading(false)
  }

  useEffect(() => {        
     if (currentLpId !== lpContext.LP.id) {      

        setCurrentLpId(lpContext.LP.id)

      lpLoaderResetList(currentLpId !== lpContext.LP.id)
    }

  }, [lpContext.LP, isFiltering])


  useEffect(() => {
      resetList(currentLpId !== lpContext.LP.id);
  }, [showRemoved, isReversed])

  useEffect(() => {
    applyFiltersAndSorting(true);
  }, [filterValues, sortValue])

  const activeFetch = (startIndex: number, stopIndex: number, changedLP: boolean = false) => {    
    setLoading(true)

    return getASNs(0)
      .then((response: any) => {        
        setLoading(false)
        if (response.is_error) {
          setError(true)
        } else {
          setError(false)

          if (response.content.value.length > 0) {
            const newValues = response.content.value.filter((asn: any) => {
              if (showRemoved || filterValues.some(a => (a.refiner === PrimaryRefiners.OrderStatus) && (a.value === "REMOVED"))) {
                return true
              } else {
                return asn.orderStatus.toLowerCase().trim() !== ASN.Status.removed;
              }
            });

            if (!newValues.length || (response.content.value.length < ASN.DefaultPageSize)) {
              setHasMore(false);
            } else {
              setHasMore(true);
            }

            setOffset(response.content.value.length)
            setAsn_list(newValues)

          } else if (((filterValues.length > 0)
                          || changedLP)
                      && response.content.value.length === 0) {
            setAsn_list([]);
            setHasMore(false);
          } else {
            setHasMore(false);
          }
        }
      })
      .catch((e) => {
        setError(true)
        setLoading(false)
      })
  }

  /**
   * lazy fetch
   * @param startIndex unused as we are filtering on client side (meaning startIndex will be off)
   * @param stopIndex also unusred as pagesize managed statically
   * @returns (indirectly) new rows
   */
  const lazyFetch = (start: number, stop: number) => {
   if (!hasMore) {
      return Promise.resolve();
    }

    setLoading(true)

    return getASNs(Math.max(offset))
      .then((response) => {
        setLoading(false)

        if (response.is_error) {
          setError(true)
        } else {
          setError(false)

          if (response.content.value.length > 0) {
            const newValues = response.content.value.filter((asn: any) => {

              if (showRemoved) {
                return true
              } else {
                if (
                  asn.orderStatus.toLowerCase().trim() !== ASN.Status.removed
                ) {
                  return true
                }
              }

              return false
            })

            if (!newValues.length || (response.content.value.length < ASN.DefaultPageSize)) {
              setHasMore(false);
            } else {
              setHasMore(true);
            }

            setOffset(offset + response.content.value.length)
            setAsn_list((asn_list) => [...asn_list, ...newValues])

          } else {
            setHasMore(false)
          }
        }
      })
      .catch((e) => {
        setError(true)
        setLoading(false)
      })
  }

  const applyFiltersAndSorting = (lpChanged: boolean = false) => {    
    setIsFiltering(true);
    resetList(lpChanged)
  }

  const clearFiltersAndSorting = () => {
    setIsFiltering(false);
    window.location.reload()
    resetList();

  }

  const [modalType, setModalType] = useState(MODAL_TYPE.CANCEL)
  const [modalOpen, setModalOpen] = useState(false)
  const [selectedAsn, setSelectedAsn] = useState({})

  if (lpContext.LP.id === DEFAULT_LP.id) {
    return <div className="ASNList container"><div>Please select an LP</div></div>
  }

  return (
    <AsnDialogContext.Provider
      value={{
        isOpen: modalOpen,
        open: () => {
          setModalOpen(true)
        },
        close: () => {
          setModalOpen(false)
          resetList()
        },
        asn: selectedAsn,
        setASN: setSelectedAsn,
        mode: modalType,
        setMode: setModalType,
      }}
    >
      <div className="ASNList">
        <CallToken />
        <div className="row" style={{ margin: '10px 0' }}>
          <div className="col-md">
            <h1>
              ASN List
              {lpContext.LP.id !== DEFAULT_LP.id
                ? ` - ${lpContext.LP.id}`
                : ' - All LPs'}
            </h1>
          </div>
          <div>
            <div className="float-right">
              {CanEdit(roleContext.roles) && lpContext.LP.id !== DEFAULT_LP.id && (
                <Link to={`/asn/create/${lpContext.LP.id}/`}>
                  <button className="btn btn-orange">+ Create ASN</button>
                </Link>
              )}
              {lpContext.LP.id !== DEFAULT_LP.id && (
                <Link to={`/asn/${lpContext.LP.id}/lotCodes`}>
                  <button
                    className="btn btn-orange"
                    style={{ marginLeft: '10px' }}
                  >
                    <FontAwesomeIcon icon={faSlidersH} style={{ fontSize: ".75em", marginBottom:".125em"} } /> Manage Lot Codes
                  </button>
                </Link>
              )}
            </div>
            <div className="custom-control custom-checkbox float-right" style={{ margin: '17px 5px' }}>
              <input
                type="checkbox"
                className="custom-control-input"
                id="checkRemoved"
                checked={showRemoved}
                onChange={() => {
                  setAsn_list([])
                  setShowRemoved(!showRemoved)
                }}
              />
              <label className="custom-control-label" htmlFor="checkRemoved">
                Show Removed
              </label>
            </div>
          </div>
        </div>
       { lpContext.LP.id !== DEFAULT_LP.id && (

        <div className="row filterOptions" style={{ margin: '10px 0' }}>

          {/*<PrimaryASNFilterDropdown*/}
          {/*  lpId={lpContext.LP.id}*/}
          {/*  setFilterValue={setFilterValue}*/}
          {/*  setPrimaryFilterValue={setPrimaryFilterValue}*/}
            {/*/>*/}
            {[{ displayName: 'ASN Number', value: 'asnnumber' }, { displayName: 'Order Status', value: 'orderstatus' }, { displayName: 'PO Number', value: 'ponumber' }].map((currDropdown: singleModeOption) => <PrimaryASNFilterDropdown singleMode={currDropdown} key={currDropdown.value} lpId={lpContext.LP.id} setPrimaryFilterValue={() => { }} setFilterValue={(value) => updateFilters(currDropdown.value, value)} />)}
          <div className="col-md">
            <SortingDropdown selectFunction={setSortValue} />
          </div>
          <div className="col-md">
            <div className="custom-control custom-checkbox float-center" style={{ paddingTop:"40px" }}>
              <input
                type="checkbox"
                className="custom-control-input"
                id="checkReversed"
                checked={isReversed}
                  onChange={() => {
                    setAsn_list([])
                    setIsReversed(!isReversed)

                }}
              />
              <label className="custom-control-label" htmlFor="checkReversed">
                Reverse Order
              </label>
              </div>

          </div>
          <div className="col-md">
              <div style={{ "paddingTop": "23px" }}>
                {!loading ? "" : <FontAwesomeIcon icon="spinner" spin />}
                {(loading ||!isFiltering) ? "" : < button className="btn btn-orange" style={{ "width": "100%" }} onClick={clearFiltersAndSorting}>Reset</button>}

            </div>
          </div>

        </div>
       )}

        <div className="ASNHeader">
          <div>ID</div>
          <div>Status</div>
          <div>PO #</div>
          <div>ASN #</div>
          <div>Created</div>
          <div>ETA</div>
          <div>{useCases ? "Cases" : "Total Units"}</div>
          <div>Actions</div>
        </div>

        {/*
         * There's a bug with the infinite scroll list where if the initial load is empty, then
           future attempts to load the list do not occur.  This causes the entire component to re-render
           to force the initial request.
         */}
        {(asn_list.length > 0 || hasMore) && (
          <InfiniteScrollWrapper
            userRoles={roleContext.roles}
            isNextPageLoading={loading}
            items={asn_list}
            hasNextPage={hasMore}
            loadNextPage={(start, stop) => {
              if (loading)
                return Promise.resolve();
              return lazyFetch(start, stop);
            }}
          />
        )}
      </div>

      <AsnDialogContext.Consumer>
        {({ mode, asn, isOpen, close }) => {
          return (
            <GenericModal isOpen={isOpen}>
              <AsnDialog mode={mode} asn={asn} close={close} />
            </GenericModal>
          )
        }}
      </AsnDialogContext.Consumer>
    </AsnDialogContext.Provider>
  )
}

export default ASNList
